/* eslint-disable operator-linebreak */
/* eslint-disable indent */
import React from 'react';
import { FormApi } from 'final-form';
import Input, { AmountInput, InputWithSelect, Select, SelectInnerItem, SelectItem } from 'ui/Input';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import { IWalletState, IFormValues, FindBenNameType, IHandleFieldsChange } from './MobileMoneyTransfer.model';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const onSelectNetwork =
  (form: FormType, findBeneficiaryName: FindBenNameType, handleFieldsChange: IHandleFieldsChange) =>
  ({ target }) => {
    const { value: selectedNetwork, name: networkFieldName } = target as HTMLInputElement;
    form.mutators.changeFieldValue(networkFieldName, selectedNetwork);
    const beneficiaryWalletNumber = form.getState().values.phoneNumber;
    if (beneficiaryWalletNumber) {
      findBeneficiaryName(beneficiaryWalletNumber, selectedNetwork);
    }
    handleFieldsChange();
  };

export const renderNetworkField = (
  networks: IWalletState['networks'],
  handleFieldsChange?: IHandleFieldsChange,
  isLookingUp?: IWalletState['isLookingUp'],
  form?: FormType,
  findBeneficiaryName?: FindBenNameType,
) => (
  <Select
    name="networkCode"
    label="Network"
    disabled={isLookingUp}
    SelectProps={
      form && findBeneficiaryName && handleFieldsChange
        ? {
            onChange: onSelectNetwork(form, findBeneficiaryName, handleFieldsChange),
          }
        : undefined
    }
  >
    {networks.map((network) => (
      <SelectItem value={network.code} key={network.name}>
        {network.name}
      </SelectItem>
    ))}
  </Select>
);

export const renderSourceAccountField = (
  accounts: IWalletState['accounts'],
  handleFieldsChange: IHandleFieldsChange,
) => (
  <Select name="sourceAccount" label="Source Account" onBlur={handleFieldsChange}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

export const renderBenWalletNumberField = (
  values: IFormValues,
  { beneficiaries, accountName, lastLookedUpPhone, isLookingUp }: IWalletState,
  form: FormType,
  findBeneficiaryName: FindBenNameType,
  handleFieldsChange: IHandleFieldsChange,
) => {
  const handleBlur = () => {
    if (values) {
      const { phoneNumber, networkCode } = values;
      findBeneficiaryName(phoneNumber, networkCode);
    }
    handleFieldsChange();
  };

  const handlePhoneSelect = ({ target }) => {
    const { value: selectedPhone } = target as HTMLInputElement;
    const beneficiary = beneficiaries.find(({ phoneNumber }) => selectedPhone === phoneNumber);
    if (beneficiary) {
      form.mutators.changeFieldValue('networkCode', beneficiary.networkCode);
      findBeneficiaryName(selectedPhone, beneficiary.networkCode);
    }
    setTimeout(() => {
      handleFieldsChange();
    }, 200);
  };

  const inputValue = values.phoneNumber;
  const showLoader = Boolean(!accountName && isLookingUp);
  const showMessage = Boolean(lastLookedUpPhone) && lastLookedUpPhone === inputValue;
  const handleClear = () => {
    form.mutators.changeFieldValue('phoneNumber', '');
  };
  return (
    <>
      <InputWithSelect
        name="phoneNumber"
        label="Beneficiary Wallet Number"
        fieldProps={{
          parse: (value: string) => onlyNCharacters(10)(onlyNumbers(value)),
        }}
        isEmpty={!inputValue}
        isLoading={showLoader}
        disabled={showLoader}
        showMessage={showMessage}
        message={accountName}
        onClear={handleClear}
        onBlur={handleBlur}
        onSelectItemChange={handlePhoneSelect}
      >
        {beneficiaries.map(({ name, phoneNumber }) => (
          <SelectItem value={phoneNumber} key={phoneNumber}>
            <SelectInnerItem label={name} value={phoneNumber} />
          </SelectItem>
        ))}
      </InputWithSelect>
    </>
  );
};

export const renderAmountField = (handleFieldsChange: IHandleFieldsChange) => (
  <AmountInput name="amount" label="Amount" onBlur={handleFieldsChange} />
);

export const renderElevyField = () => <AmountInput name="eLevyCharge" label="E-Levy Fee" disabled />;
export const renderNarrationField = () => (
  <Input
    name="narration"
    label="Narration"
    fieldProps={{
  parse: onlyNCharacters(25),
}}
  />
);

const renderWalletField = (
  values: IFormValues,
  state: IWalletState,
  form: FormType,
  findBeneficiaryName: FindBenNameType,
  handleFieldsChange: IHandleFieldsChange,
) => {
  const fields = (
    <>
      {renderNetworkField(state.networks, handleFieldsChange, state.isLookingUp, form, findBeneficiaryName)}
      {renderSourceAccountField(state.accounts, handleFieldsChange)}
      {renderBenWalletNumberField(values, state, form, findBeneficiaryName, handleFieldsChange)}
      {renderAmountField(handleFieldsChange)}
      {renderNarrationField()}
      {renderElevyField()}
    </>
  );

  return fields;
};

export default renderWalletField;
